import React, { useMemo, Fragment } from 'react';
import {
	Card,
	CardBody,
	Button,
	Pagination,
	PaginationItem,
	PaginationLink,
} from 'reactstrap';
import { Colxx } from '../../../../../../../components/common/CustomBootstrap';
import ReactTable from 'react-table';
import UpdateReservation from '../UpdateReservation';

const ContactIcons = ({ hasEmail, hasPhone }) => {
	if (!hasEmail && !hasPhone) {
		return <span>-</span>;
	}
	return (
		<div className="d-flex align-items-center">
			{hasEmail && (
				<i
					className="simple-icon-envelope text-primary mr-2"
					title="Email available"
					style={{ fontSize: 16 }}
				/>
			)}
			{hasPhone && (
				<i
					className="simple-icon-phone text-primary"
					title="Phone available"
					style={{ fontSize: 16 }}
				/>
			)}
		</div>
	);
};

const ReservationPagination = ({
	page,
	totalPages,
	onPageChange,
	pagesPerChunk,
}) => {
	const chunkSize = Math.max(1, pagesPerChunk || 1);
	const chunkStart = Math.floor(page / chunkSize) * chunkSize;
	const chunkEnd = Math.min(chunkStart + chunkSize, totalPages);
	const canPrevious = page > 0;
	const canNext = totalPages ? page < totalPages - 1 : false;

	const renderPageButtons = () => {
		const items = [];
		for (let i = chunkStart; i < chunkEnd; i += 1) {
			items.push(
				<PaginationItem key={i} active={i === page}>
					<PaginationLink
						href="#"
						onClick={(e) => {
							e.preventDefault();
							onPageChange(i);
						}}
					>
						{i + 1}
					</PaginationLink>
				</PaginationItem>
			);
		}
		return items;
	};

	return (
		<div className="d-flex flex-column align-items-center mt-3">
			<Pagination size="sm" className="mb-2">
				<PaginationItem disabled={!canPrevious}>
					<PaginationLink
						href="#"
						onClick={() => {
							if (canPrevious) onPageChange(page - 1);
						}}
					>
						<i className="simple-icon-arrow-left" />
					</PaginationLink>
				</PaginationItem>
				{renderPageButtons()}
				<PaginationItem disabled={!canNext}>
					<PaginationLink
						href="#"
						onClick={() => {
							if (canNext) onPageChange(page + 1);
						}}
					>
						<i className="simple-icon-arrow-right" />
					</PaginationLink>
				</PaginationItem>
			</Pagination>
			<span className="text-muted text-small">
				Page {totalPages ? Math.min(page + 1, totalPages) : 0} of {totalPages || 0}
			</span>
		</div>
	);
};

export default function ReservationList({
	data = [],
	loading,
	onRefresh,
	page = 0,
	pageSize = 10,
	totalPages = 0,
	totalItems = 0,
	pagesPerChunk = 5,
	onPageChange = () => { },
}) {
	const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);


	const columns = useMemo(
		() => [
			{
				Header: 'Name',
				accessor: 'displayName',
				Cell: (props) => <p className="mb-0">{props.value}</p>,
				style: { whiteSpace: 'unset' },
			},
			{
				Header: 'Reference Number',
				accessor: 'referenceNumber',
				Cell: (props) => <p className="mb-0">{props.value || '-'}</p>,
				style: { whiteSpace: 'unset' },
			},
			{
				Header: 'Dates',
				accessor: 'datesRange',
				Cell: (props) => <p className="mb-0">{props.value || '-'}</p>,
				style: { whiteSpace: 'unset' },
			},
			{
				Header: 'Contact Methods',
				id: 'contactMethods',
				Cell: (props) => (
					<ContactIcons hasEmail={props.original.hasEmail} hasPhone={props.original.hasPhone} />
				),
				width: 140,
			},
			// {
			// 	Header: 'Email',
			// 	accessor: 'email',
			// 	Cell: (props) => <p className="mb-0">{props.value || '-'}</p>,
			// 	style: { whiteSpace: 'unset' },
			// },
			// {
			// 	Header: 'Phone #',
			// 	accessor: 'phone',
			// 	Cell: (props) => <p className="mb-0">{props.value || '-'}</p>,
			// 	width: 160,
			// },
			{
				Header: 'Recent Comms',
				accessor: 'recentComms',
				Cell: (props) => <p className="mb-0">{props.value || '-'}</p>,
				style: { whiteSpace: 'unset' },
			},
			{
				Header: 'Status',
				accessor: 'status',
				Cell: (props) => <p className="mb-0">{props.value}</p>,
				width: 160,
			},
			{
				Header: 'Actions',
				id: 'actions',
				width: 200,
				Cell: (props) => {
					return (
						<div
							className="d-flex align-items-center justify-content-start"
							style={{ gap: "8px" }}
						>
							{/* Edit Button / Component */}
							<UpdateReservation
								row={props.original}
								onSuccess={onRefresh}
							/>

							{/* Open Contact Button */}
							<Button
								outline
								size="xs"
								color="primary"
								className="px-3"
							>
								<i className="simple-icon-action-redo mr-1" />
								Open Contact
							</Button>
						</div>
					);
				},
			},

		],
		[onRefresh]
	);
	if (loading) {
		return (
			<Card className="d-flex flex-row mb-3">
				<CardBody>
					<h4 style={{ textAlign: 'center' }}>Loading...</h4>
				</CardBody>
			</Card>
		);
	}
	return (
		<Fragment>
			<Colxx xxs="12" className="mb-2">
				<Card className="mb-2">
					<CardBody>
						<Fragment>
							<ReactTable
								data={rows}
								columns={columns}
								defaultPageSize={pageSize}
								showPagination={false}
								filterable={false}
								className="-striped -highlight -bordered"
								minRows={rows.length ? undefined : pageSize}
								noDataText="No reservations found"
							/>
							<ReservationPagination
								page={page}
								totalPages={totalPages}
								pagesPerChunk={pagesPerChunk}
								onPageChange={onPageChange}
							/>
							<div className="text-muted text-right mt-2">
								Showing page {totalPages ? page + 1 : 0} of {totalPages || 0} ({totalItems} total)
							</div>
						</Fragment>

					</CardBody>
				</Card>
			</Colxx>
		</Fragment>
	);
}

